import React, { useEffect, useMemo, useState } from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import styled, { css } from 'styled-components';

import {
  layoutDirState,
  layoutPaddingState,
  layoutTypeState,
  layoutWidthState,
  menuHeightState,
  menuWidthState,
  showBlogSlideState,
  showSideState,
} from '@atom/setting/homepageSet';
import { showNavState, subMenuMobileHeightState } from '@atom/setting/navigation';
import NavWrapper from '@components/wrapper/NavWrapper';
import SubMenuList from '@components/navigation/common/SubMenuList';
import RootRoute from '@routes/RootRoute';
import '@components/library/Quill/QuillStyle.css';
import { fuego } from '@store/firebase';
import { cookieStore } from '@store/cookie/cookieStore';
import { useCollection, useDocument } from '@nandorojo/swr-firestore';
import { User } from '@typings/setting/userType.interface';
import { CollectionPath } from '@typings/collections.enum';
import {
  authInitializedState,
  currentUserAuthTypeState,
  currentUserDataState,
  currentUserIDState,
  currentUserWalletState,
} from '@atom/setting/user';
import { AUTH_TYPE } from '@typings/common/authorizationType';
import { LAYOUT_TYPE, LAYOUT_TYPE_VALUE } from '@typings/setting/layoutType';
import Aside from '@template/Aside';
import { LAYOUT_DIR, LAYOUT_DIR_VALUE } from '@typings/setting/layoutDir';
import useWindowWidth from '@hooks/useWindowWidth';
import Slider from '@components/dataDisplay/slider/Slider';
import { IslideItem } from '@typings/setting/slideItem';
import MiniNavBar from '@components/navigation/common/MiniNavBar';
import { IhomepageSetting } from '@typings/setting/homepageSetting.interface';
import { scrollbarStyle } from '@styles/variants/scrollbar';
import Theme from '@styles/Theme';
import { formatPixelValue } from '@utils/formatPixelValue';

// 레이아웃 타입별 기본 스타일 매핑
const layoutBaseStyleMap = {
  [LAYOUT_TYPE.TOP]: css`
    width: 100%;
    margin: 0 auto;
    min-height: 100dvh;
    display: grid;
    grid-template-areas: 'nav' 'contents';
    grid-template-rows: auto 1fr;
  `,
  [LAYOUT_TYPE.LEFT]: css`
    width: 100%;
    margin: 0 auto;
    display: grid;
    min-height: 100dvh;
    gap: 24px;
  `,
  [LAYOUT_TYPE.MINIHOME]: css`
    width: 100%;
    margin: 0 auto;
    display: grid;
    min-height: 100dvh;
    gap: 24px;
  `,
  [LAYOUT_TYPE.THREEDAN]: css`
    width: 100%;
    min-height: 100dvh;
    margin: 0 auto;
    display: grid;
    justify-content: center;
    gap: 24px;
  `,
  [LAYOUT_TYPE.BLOG]: css`
    width: 100%;
    margin: 0 auto;
    display: grid;
    gap: 16px;
  `,
  [LAYOUT_TYPE.MEMO]: css`
    width: 100%;
    height: 90dvh;
    margin: 45px auto;
    display: grid;
    border-radius: var(--cardBorderRadius);
    background: var(--backgroundColor);
    border: 1px solid var(--lineColor);
    box-shadow: ${Theme.shadow.card};
    gap: 0;
    overflow: hidden;

    @media (max-width: 480px) {
      margin: 0 auto;
      border-radius: 0;
      background-color: transparent;
      border: 0;
      box-shadow: none;
      overflow: unset;
      height: auto;
    }
  `,
  [LAYOUT_TYPE.GAMEUI]: css`
    width: 100%;
    min-height: 100dvh; // 스티커보드때문에
    min-width: unset;
    max-width: unset;
    margin: 0 auto;
    display: unset;
  `,
  [LAYOUT_TYPE.UNSET]: css`
    width: 100%;
    min-width: unset;
    max-width: unset;
    margin: 0 auto;
    display: unset;
  `,
};

// 레이아웃 방향별 그리드 템플릿 매핑
const layoutDirectionMap = {
  // LEFT 레이아웃
  [`${LAYOUT_TYPE.LEFT}_${LAYOUT_DIR.LEFT}`]: (menuWidth: string) => css`
    grid-template-areas: 'nav contents';
    grid-template-columns: ${menuWidth} 1fr;
  `,
  [`${LAYOUT_TYPE.LEFT}_${LAYOUT_DIR.RIGHT}`]: (menuWidth: string) => css`
    grid-template-areas: 'contents nav';
    grid-template-columns: 1fr ${menuWidth};
  `,

  // MINIHOME 레이아웃
  [`${LAYOUT_TYPE.MINIHOME}_${LAYOUT_DIR.LEFT}`]: (menuWidth: string) => css`
    grid-template-areas: 'nav contents';
    grid-template-columns: ${menuWidth} 1fr;
  `,
  [`${LAYOUT_TYPE.MINIHOME}_${LAYOUT_DIR.RIGHT}`]: (menuWidth: string) => css`
    grid-template-areas: 'contents nav';
    grid-template-columns: 1fr ${menuWidth};
  `,

  // THREEDAN 레이아웃
  [`${LAYOUT_TYPE.THREEDAN}_${LAYOUT_DIR.LEFT}`]: (menuWidth: string) => css`
    grid-template-areas: 'nav contents aside';
    grid-template-columns: ${menuWidth} 700px 260px;
  `,
  [`${LAYOUT_TYPE.THREEDAN}_${LAYOUT_DIR.RIGHT}`]: (menuWidth: string) => css`
    grid-template-areas: 'aside contents nav';
    grid-template-columns: 260px 600px ${menuWidth};
  `,

  // MEMO 레이아웃
  [`${LAYOUT_TYPE.MEMO}_${LAYOUT_DIR.LEFT}`]: (
    menuWidth: string,
    aside: string,
  ) => css`
    grid-template-columns: ${menuWidth} 1fr 260px;
    grid-template-rows: auto 1fr;
    grid-template-areas:
      'info info info'
      'nav contents ${aside}';
  `,
  [`${LAYOUT_TYPE.MEMO}_${LAYOUT_DIR.RIGHT}`]: (
    menuWidth: string,
    aside: string,
  ) => css`
    grid-template-columns: 260px 1fr ${menuWidth};
    grid-template-rows: auto 1fr;
    grid-template-areas:
      'info info info'
      '${aside} contents nav';
  `,

  // BLOG 레이아웃
  [`${LAYOUT_TYPE.BLOG}_${LAYOUT_DIR.LEFT}`]: (
    menuWidth: string,
    aside: string,
    blogSlide: boolean,
  ) => css`
    grid-template-rows: ${blogSlide ? '300px auto' : 'auto'};
    grid-template-columns: ${menuWidth} 1fr 1fr 260px;
    grid-template-areas: ${blogSlide
      ? `'slide slide slide slide'
           'nav contents contents ${aside}'`
      : `'nav contents contents ${aside}'`};
    margin-top: ${blogSlide ? '0' : '42px'};
  `,
  [`${LAYOUT_TYPE.BLOG}_${LAYOUT_DIR.RIGHT}`]: (
    menuWidth: string,
    aside: string,
    blogSlide: boolean,
  ) => css`
    grid-template-rows: ${blogSlide ? '300px auto' : 'auto'};
    grid-template-columns: 260px 1fr 1fr ${menuWidth};
    grid-template-areas: ${blogSlide
      ? `'slide slide slide slide'
           '${aside} contents contents nav'`
      : `'${aside} contents contents nav'`};
    margin-top: ${blogSlide ? '0' : '42px'};
  `,
};

// 콘텐츠 영역 스타일 매핑
const contentsStyleMap = {
  [LAYOUT_TYPE.MEMO]: css`
    display: block;
    justify-content: unset;
    flex: unset;
    overflow-y: auto;
    height: 100%;
    min-height: 0;
    ${scrollbarStyle}
  `,
  [LAYOUT_TYPE.BLOG]: css`
    display: block;
    justify-content: unset;
    flex: unset;
    overflow-y: visible;
    height: auto;
    min-height: fit-content;
  `,
  [LAYOUT_TYPE.LEFT]: css`
    display: flex;
    justify-content: center;
    flex: 1;
    overflow-y: visible;
    min-height: fit-content;
  `,
  [LAYOUT_TYPE.MINIHOME]: css`
    display: flex;
    justify-content: center;
    flex: 1;
    overflow-y: visible;
    min-height: fit-content;
  `,
  [LAYOUT_TYPE.THREEDAN]: css`
    display: flex;
    justify-content: center;
    flex: 1;
    overflow-y: visible;
    min-height: fit-content;
  `,
  [LAYOUT_TYPE.TOP]: css`
    display: flex;
    justify-content: center;
    flex: 1;
    overflow-y: visible;
    min-height: fit-content;
  `,
  [LAYOUT_TYPE.GAMEUI]: css`
    display: flex;
    justify-content: center;
    flex: 1;
    overflow-y: visible;
    min-height: fit-content;
    height: 100dvh;
  `,
  [LAYOUT_TYPE.UNSET]: css`
    display: flex;
    justify-content: center;
    flex: 1;
    overflow-y: visible;
    min-height: fit-content;
  `,
};

// 콘텐츠 보더 스타일 매핑
const contentsBorderMap = {
  [`${LAYOUT_TYPE.THREEDAN}_${LAYOUT_DIR.LEFT}`]: css`
    border-left: 1px solid var(--lineColor);
    border-right: 1px solid var(--lineColor);
    @media (max-width: 480px) {
      border: 0;
    }
  `,
  [`${LAYOUT_TYPE.THREEDAN}_${LAYOUT_DIR.RIGHT}`]: css`
    border-left: 1px solid var(--lineColor);
    border-right: 1px solid var(--lineColor);
    @media (max-width: 480px) {
      border: 0;
    }
  `,
  [`${LAYOUT_TYPE.MEMO}_${LAYOUT_DIR.LEFT}`]: css`
    border-left: 1px solid var(--lineColor);
    @media (max-width: 480px) {
      border: 0;
    }
  `,
  [`${LAYOUT_TYPE.MEMO}_${LAYOUT_DIR.RIGHT}`]: css`
    border-right: 1px solid var(--lineColor);
    @media (max-width: 480px) {
      border: 0;
    }
  `,
  [`${LAYOUT_TYPE.UNSET}_${LAYOUT_DIR.LEFT}`]: css`
    max-width: 100%;
  `,
  [`${LAYOUT_TYPE.UNSET}_${LAYOUT_DIR.RIGHT}`]: css`
    max-width: 100%;
  `,
};

// paddingTop 계산 로직 매핑
const paddingTopCalculator = {
  [LAYOUT_TYPE.TOP]: (menuHeight: string, showNav: boolean, isMobile: boolean) =>
    showNav
      ? isMobile
        ? 'calc(60px + env(safe-area-inset-top))'
        : menuHeight && menuHeight !== '0px'
          ? `calc(${menuHeight} + env(safe-area-inset-top))`
          : 'calc(80px + env(safe-area-inset-top))'
      : '0px',
  [LAYOUT_TYPE.MEMO]: () => '0px',
  [LAYOUT_TYPE.UNSET]: () => '0px',
  default: (menuHeight: string, showNav: boolean, isMobile: boolean) =>
    !showNav ? '0px' : isMobile ? 'calc(60px + env(safe-area-inset-top))' : '0px',
};

// 레이아웃별 추가 컴포넌트 렌더링 조건 매핑
const additionalComponentsMap = {
  [LAYOUT_TYPE.MEMO]: {
    showMiniNavBar: true,
    showSlider: false,
    showAside: true,
  },
  [LAYOUT_TYPE.BLOG]: {
    showMiniNavBar: false,
    showSlider: true,
    showAside: true,
  },
  [LAYOUT_TYPE.THREEDAN]: {
    showMiniNavBar: false,
    showSlider: false,
    showAside: true,
  },
  default: {
    showMiniNavBar: false,
    showSlider: false,
    showAside: false,
  },
};

// 스타일 적용 헬퍼 함수들
const getLayoutStyle = (layoutType: LAYOUT_TYPE_VALUE, showNav: boolean) => {
  return layoutType && showNav
    ? layoutBaseStyleMap[layoutType]
    : layoutBaseStyleMap[LAYOUT_TYPE.UNSET];
};

const getDirectionStyle = (
  layoutType: LAYOUT_TYPE_VALUE,
  layoutDir: LAYOUT_DIR_VALUE,
  menuWidth: string,
  aside: string,
  blogSlide: boolean,
) => {
  const key = `${layoutType}_${layoutDir}`;
  const styleFunction = layoutDirectionMap[key];
  return styleFunction ? styleFunction(menuWidth, aside, blogSlide) : null;
};

const getContentsStyle = (layoutType: LAYOUT_TYPE_VALUE) => {
  return contentsStyleMap[layoutType] || contentsStyleMap[LAYOUT_TYPE.UNSET];
};

const getContentsBorderStyle = (
  layoutType: LAYOUT_TYPE_VALUE,
  layoutDir: LAYOUT_DIR_VALUE,
  showNav: boolean,
) => {
  if (!showNav) return contentsBorderMap[`${LAYOUT_TYPE.UNSET}_${layoutDir}`];
  return contentsBorderMap[`${layoutType}_${layoutDir}`] || null;
};

const calculatePaddingTop = (
  layoutType: LAYOUT_TYPE_VALUE,
  menuHeight: string,
  showNav: boolean,
  isMobile: boolean,
) => {
  const calculator =
    paddingTopCalculator[layoutType] || paddingTopCalculator.default;
  return calculator(menuHeight, showNav, isMobile);
};

const getAdditionalComponents = (layoutType: LAYOUT_TYPE_VALUE) => {
  return additionalComponentsMap[layoutType] || additionalComponentsMap.default;
};

const Layout = styled.div<{
  showNav: boolean | null | undefined;
  layoutType: LAYOUT_TYPE_VALUE;
  layoutDir: LAYOUT_DIR_VALUE;
  menuWidth: string;
  layoutWidth: string;
  aside: string;
  blogSlide: boolean;
}>`
  max-width: ${({ layoutWidth }) => layoutWidth};

  // 기본 레이아웃 스타일 적용
  ${({ layoutType, showNav }) => getLayoutStyle(layoutType, showNav!)}

  // 방향별 그리드 템플릿 적용
  ${({ layoutType, layoutDir, menuWidth, aside, blogSlide }) =>
    getDirectionStyle(layoutType, layoutDir, menuWidth, aside, blogSlide)}

  @media (max-width: 768px) {
    display: unset;
    min-width: unset;
    max-width: 100%;
  }
`;

const Contents = styled.div<{
  showNav: boolean | null | undefined;
  layoutType: LAYOUT_TYPE_VALUE;
  layoutDir: LAYOUT_DIR_VALUE;
}>`
  grid-area: contents;
  width: 100%;

  // 레이아웃별 콘텐츠 스타일 적용
  ${({ layoutType }) => getContentsStyle(layoutType)}

  // 네비게이션 숨김 시 보더 제거
  ${({ showNav }) => !showNav && 'border: 0px;'}
  
  // 레이아웃별 보더 스타일 적용
  ${({ layoutType, layoutDir, showNav }) =>
    getContentsBorderStyle(layoutType, layoutDir, showNav!)}

  @media (max-width: 480px) {
    padding: 0 !important;
    height: auto;
    overflow-y: visible;
    display: flex;
    justify-content: center;
    flex: 1;
    min-height: fit-content;
  }
`;

function LayoutWrapper({ homeSet }: { homeSet?: IhomepageSetting | null }) {
  const showNav = useRecoilValue(showNavState);
  const layoutType = useRecoilValue(layoutTypeState);
  const layoutDir = useRecoilValue(layoutDirState);
  const showSide = useRecoilValue(showSideState);
  const menuHeight = useRecoilValue(menuHeightState);
  const menuWidth = useRecoilValue(menuWidthState);
  const layoutWidth = useRecoilValue(layoutWidthState);
  const blogSlide = useRecoilValue(showBlogSlideState);
  const layoutPadding = useRecoilValue<any>(layoutPaddingState);
  const isMobile = useWindowWidth() < 480;
  const subMenuMobileHeight = useRecoilValue(subMenuMobileHeightState);

  // paddingTop 동기 계산 (useMemo로 1프레임 지연 제거)
  const currentPaddingTop = useMemo(
    () => calculatePaddingTop(layoutType, menuHeight, showNav!, isMobile),
    [layoutType, menuHeight, showNav, isMobile],
  );

  // 사용자 인증 관련 로직
  const [resolvedUserId, setResolvedUserId] = useState<string | null>(null);
  const [authResolved, setAuthResolved] = useState(false);
  const cashedUserId = cookieStore.getUserToken();
  const setCurrentUserUID = useSetRecoilState(currentUserIDState);
  const setCurrentUserData = useSetRecoilState(currentUserDataState);
  const sestCurrentUserWallet = useSetRecoilState(currentUserWalletState);
  const setCurrentUserAuthType = useSetRecoilState(currentUserAuthTypeState);
  const setAuthInitialized = useSetRecoilState(authInitializedState);

  // Firebase Auth 초기화 완료 시점을 onAuthStateChanged로 정확히 감지
  useEffect(() => {
    const unsubscribe = fuego.auth().onAuthStateChanged((user) => {
      setResolvedUserId(user?.uid || null);
      setAuthResolved(true);
      setAuthInitialized(true);
    });
    return () => unsubscribe();
  }, [setAuthInitialized]);

  const { data: currentUserData } = useDocument<User>(
    resolvedUserId ? `${CollectionPath.USERS}/${resolvedUserId}` : null,
    { listen: true },
  );
  const { data: slideList } = useCollection<IslideItem>(
    `${CollectionPath.SLIDE}`,
    { listen: true, orderBy: ['order', 'asc'] },
  );

  useEffect(() => {
    if (!authResolved) return; // Auth 초기화 전에는 상태 변경 안 함
    if (resolvedUserId && cashedUserId && currentUserData) {
      setCurrentUserUID(resolvedUserId);
      setCurrentUserData(currentUserData);
      sestCurrentUserWallet(currentUserData!.wallet);
      setCurrentUserAuthType(currentUserData!.authorizationType);
    } else if (resolvedUserId && cashedUserId && currentUserData === null) {
      // Firestore 유저 문서가 없는 경우 게스트 처리
      setCurrentUserAuthType(AUTH_TYPE.GUEST);
      setCurrentUserUID(null);
      setCurrentUserData(null);
    } else if (!resolvedUserId) {
      // 확실히 로그아웃 상태
      setCurrentUserAuthType(AUTH_TYPE.GUEST);
      setCurrentUserUID(null);
      setCurrentUserData(null);
    }
  }, [
    resolvedUserId,
    currentUserData,
    cashedUserId,
    authResolved,
    setCurrentUserData,
  ]);

  // 추가 컴포넌트 렌더링 조건 계산
  const additionalComponents = getAdditionalComponents(layoutType);
  const shouldShowAside =
    showSide && additionalComponents.showAside && !isMobile && showNav;

  return (
    <>
      <Layout
        id="layout"
        layoutType={layoutType}
        layoutDir={layoutDir}
        layoutWidth={formatPixelValue(layoutWidth, '100%')}
        menuWidth={formatPixelValue(menuWidth, '220px')}
        showNav={showNav}
        aside={showSide ? 'aside' : 'contents'}
        blogSlide={blogSlide ?? true}
      >
        {/* MiniNavBar 렌더링 */}
        {additionalComponents.showMiniNavBar && showNav && (
          <MiniNavBar
            showUser
            showTitle
            style={{
              borderBottom: '1px solid var(--lineColor)',
              gridArea: 'info',
            }}
          />
        )}

        {/* Slider 렌더링 */}
        {additionalComponents.showSlider && blogSlide && showNav && (
          <Slider
            list={slideList || []}
            style={{
              gridArea: 'slide',
              height: '300px',
              borderRadius: 0,
            }}
          />
        )}

        {/* Navigation — 항상 마운트, CSS hidden으로 전환하여 재마운트 비용 제거 */}
        <NavWrapper layoutType={layoutType} homeSet={homeSet} hidden={!showNav} />

        {/* Contents 렌더링 */}
        <Contents
          id="layoutContents"
          style={
            layoutPadding
              ? {
                paddingTop:
                  layoutType === LAYOUT_TYPE.MEMO ? '0px' : currentPaddingTop,
                padding: layoutPadding,
                ...(isMobile && subMenuMobileHeight > 0
                  ? { paddingBottom: subMenuMobileHeight }
                  : {}),
              }
              : {
                paddingTop:
                  layoutType === LAYOUT_TYPE.MEMO ? '0px' : currentPaddingTop,
                ...(isMobile && subMenuMobileHeight > 0
                  ? { paddingBottom: subMenuMobileHeight }
                  : {}),
              }
          }
          layoutType={layoutType}
          layoutDir={layoutDir}
          showNav={showNav}
        >
          <RootRoute />
        </Contents>

        {/* Aside 렌더링 */}
        {shouldShowAside && <Aside style={{ width: `${menuWidth || 220}px` }} />}

        {layoutType !== LAYOUT_TYPE.GAMEUI && (
          <SubMenuList isVisible={true} />
        )}
      </Layout>
      {/* 서브 메뉴 렌더링 (자체적으로 리스트를 판단하여 없으면 null 반환) */}
    </>
  );
}

export default LayoutWrapper;
